import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { ensureIbransonMasonry } from './ibranson-preview-styles'

type MasonryInstance = {
  layout: () => void
  destroy: () => void
  reloadItems?: () => void
}

@Component({
  selector: 'app-ib-gallery',
  templateUrl: './ib-gallery.component.html',
  standalone: false,
})
export class IbGalleryComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input() title = 'Item'
  @Input() gallery: string[] = []
  @ViewChild('masonryGrid') masonryGrid?: ElementRef<HTMLElement>

  private masonry: MasonryInstance | null = null
  private viewReady = false
  private layoutTimers: number[] = []
  private readonly onImgLoad = () => this.scheduleLayout()

  ngAfterViewInit() {
    this.viewReady = true
    this.initMasonry()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['gallery'] && this.viewReady) {
      window.setTimeout(() => this.initMasonry(), 0)
    }
  }

  ngOnDestroy() {
    this.destroyMasonry()
  }

  tileClass(index: number): string {
    if (index === 0) {
      return 'grid-item grid-item--width3'
    }
    if (index === 1) {
      return 'grid-item grid-item--width2'
    }
    return 'grid-item'
  }

  private async initMasonry() {
    const el = this.masonryGrid?.nativeElement
    if (!el || this.gallery.length === 0) {
      this.destroyMasonry()
      return
    }
    const MasonryCtor = await ensureIbransonMasonry()
    if (!MasonryCtor) {
      return
    }
    this.destroyMasonry()
    this.masonry = new MasonryCtor(el, {
      itemSelector: '.grid-item',
      columnWidth: '.grid-sizer',
      percentPosition: true,
    }) as MasonryInstance
    el.querySelectorAll('img').forEach((img) => {
      if (img.complete) {
        return
      }
      img.addEventListener('load', this.onImgLoad)
      img.addEventListener('error', this.onImgLoad)
    })
    this.scheduleLayout()
  }

  private scheduleLayout() {
    this.layoutTimers.forEach((id) => window.clearTimeout(id))
    this.layoutTimers = [0, 200, 1000, 3000].map((delay) =>
      window.setTimeout(() => {
        this.masonry?.reloadItems?.()
        this.masonry?.layout()
      }, delay),
    )
  }

  private destroyMasonry() {
    this.layoutTimers.forEach((id) => window.clearTimeout(id))
    this.layoutTimers = []
    this.masonryGrid?.nativeElement
      ?.querySelectorAll('img')
      .forEach((img) => {
        img.removeEventListener('load', this.onImgLoad)
        img.removeEventListener('error', this.onImgLoad)
      })
    try {
      this.masonry?.destroy()
    } catch {
      /* ignore */
    }
    this.masonry = null
  }
}
